import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { X } from 'lucide-react';
import { useStores } from '@/contexts/StoreContext';
import styles from './ClipboardPreview.module.css';

interface ClipboardPreviewProps {
  isVisible: boolean;
}

interface ClipboardContent {
  type: 'image' | 'svg' | 'text' | 'empty';
  data?: string;
  preview?: string;
}

interface KeyboardShortcut {
  keys: string[];
  description: string;
  category: 'tools' | 'actions' | 'navigation' | 'stroke' | 'fill';
}

const shortcuts: KeyboardShortcut[] = [
  // Tools
  { keys: ['1'], description: 'Select Tool', category: 'tools' },
  { keys: ['2'], description: 'Rectangle Tool', category: 'tools' },
  { keys: ['3'], description: 'Ellipse Tool', category: 'tools' },
  { keys: ['4'], description: 'Diamond Tool', category: 'tools' },
  { keys: ['5'], description: 'Arrow Tool', category: 'tools' },
  { keys: ['6'], description: 'Line Tool', category: 'tools' },
  { keys: ['7'], description: 'Text Tool', category: 'tools' },
  { keys: ['8'], description: 'Icon Tool', category: 'tools' },
  { keys: ['9'], description: 'Clipboard Tool', category: 'tools' },
  
  // Actions
  { keys: ['Ctrl', 'Z'], description: 'Undo', category: 'actions' },
  { keys: ['Ctrl', 'Y'], description: 'Redo', category: 'actions' },
  { keys: ['Ctrl', 'A'], description: 'Select All', category: 'actions' },
  { keys: ['Ctrl', 'Click'], description: 'Multiselect', category: 'actions' },
  { keys: ['Ctrl', 'C'], description: 'Copy', category: 'actions' },
  { keys: ['Ctrl', 'V'], description: 'Paste', category: 'actions' },
  { keys: ['Ctrl', 'D'], description: 'Duplicate', category: 'actions' },
  { keys: ['Ctrl', 'G'], description: 'Group', category: 'actions' },
  { keys: ['Ctrl', 'Shift', 'G'], description: 'Ungroup', category: 'actions' },
  { keys: ['Del'], description: 'Delete Selected', category: 'actions' },
  
  // Stroke Colors (Q-I row)
  { keys: ['Q'], description: 'Transparent', category: 'stroke' },
  { keys: ['W'], description: 'White', category: 'stroke' },
  { keys: ['E'], description: 'Red', category: 'stroke' },
  { keys: ['R'], description: 'Teal', category: 'stroke' },
  { keys: ['T'], description: 'Blue', category: 'stroke' },
  { keys: ['Y'], description: 'Yellow', category: 'stroke' },
  { keys: ['U'], description: 'Purple', category: 'stroke' },
  { keys: ['I'], description: 'Dark Red', category: 'stroke' },
  
  // Fill Colors (A-K row)
  { keys: ['A'], description: 'Transparent', category: 'fill' },
  { keys: ['S'], description: 'White', category: 'fill' },
  { keys: ['D'], description: 'Red', category: 'fill' },
  { keys: ['F'], description: 'Teal', category: 'fill' },
  { keys: ['G'], description: 'Blue', category: 'fill' },
  { keys: ['H'], description: 'Yellow', category: 'fill' },
  { keys: ['J'], description: 'Purple', category: 'fill' },
  { keys: ['K'], description: 'Dark Red', category: 'fill' },
  
  // Navigation
  { keys: ['Space'], description: 'Pan Mode (hold)', category: 'navigation' },
  { keys: ['Esc'], description: 'Cancel/Clear Selection', category: 'navigation' },
];

const KeyboardKey: React.FC<{ keyName: string }> = ({ keyName }) => {
  return (
    <span className={styles['keyboardKey']}>
      {keyName}
    </span>
  );
};

const ShortcutItem: React.FC<{ shortcut: KeyboardShortcut }> = ({ shortcut }) => {
  return (
    <div className={styles['shortcutItem']}>
      <div className={styles['shortcutKeys']}>
        {shortcut.keys.map((key, index) => (
          <React.Fragment key={index}>
            <KeyboardKey keyName={key} />
            {index < shortcut.keys.length - 1 && <span className={styles['keySeparator']}>+</span>}
          </React.Fragment>
        ))}
      </div>
      <span className={styles['shortcutDescription']}>{shortcut.description}</span>
    </div>
  );
};

export const ClipboardPreview: React.FC<ClipboardPreviewProps> = observer(({ isVisible }) => {
  const { uiStore } = useStores();
  const [clipboardContent, setClipboardContent] = useState<ClipboardContent>({ type: 'empty' });
  const [showShortcuts, setShowShortcuts] = useState(false);

  const checkClipboard = async () => {
    try {
      // Check for images first
      try {
        const clipboardItems = await navigator.clipboard.read();
        
        for (const clipboardItem of clipboardItems) {
          for (const type of clipboardItem.types) {
            if (type.startsWith('image/')) {
              const blob = await clipboardItem.getType(type);
              const reader = new FileReader();
              
              reader.onload = () => {
                const imageData = reader.result as string;
                setClipboardContent({
                  type: 'image',
                  data: imageData,
                  preview: imageData
                });
              };
              
              reader.readAsDataURL(blob);
              return;
            }
          }
        }
      } catch (imageError) {
        // Fall through to text handling
      }

      // Check for text content
      const text = await navigator.clipboard.readText();
      
      if (text.trim().startsWith('<svg') || text.includes('</svg>')) {
        // Create a sanitized preview for SVG
        const parser = new DOMParser();
        try {
          const svgDoc = parser.parseFromString(text, 'image/svg+xml');
          const svgElement = svgDoc.querySelector('svg');
          
          if (svgElement && !svgDoc.querySelector('parsererror')) {
            // Create a safe preview - just the basic SVG structure
            const previewSvg = `<svg viewBox="${svgElement.getAttribute('viewBox') || '0 0 100 100'}" xmlns="http://www.w3.org/2000/svg">${svgElement.innerHTML}</svg>`;
            setClipboardContent({
              type: 'svg',
              data: text,
              preview: previewSvg
            });
          } else {
            setClipboardContent({ type: 'empty' });
          }
        } catch (error) {
          setClipboardContent({ type: 'empty' });
        }
      } else if (text && text.trim().length > 0 && text.trim().length < 500) {
        // Show text preview (limited length)
        setClipboardContent({
          type: 'text',
          data: text,
          preview: text.trim()
        });
      } else {
        setClipboardContent({ type: 'empty' });
      }
    } catch (error) {
      setClipboardContent({ type: 'empty' });
    }
  };

  useEffect(() => {
    if (isVisible) {
      checkClipboard();
      // Check clipboard every 2 seconds when visible
      const interval = setInterval(checkClipboard, 2000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [isVisible]);

  if (!isVisible) {
    return null;
  }

  const renderPreview = () => {
    switch (clipboardContent.type) {
      case 'image':
        return (
          <div className={styles['previewContainer']}>
            <img 
              src={clipboardContent.preview} 
              alt="Clipboard image" 
              className={styles['imagePreview']}
            />
            <span className={styles['label']}>Image ready to paste</span>
          </div>
        );
      
      case 'svg':
        return (
          <div className={styles['previewContainer']}>
            <div 
              className={styles['svgPreview']}
              dangerouslySetInnerHTML={{ __html: clipboardContent.preview || '' }}
            />
            <span className={styles['label']}>SVG ready to paste</span>
          </div>
        );
      
      case 'text':
        return (
          <div className={styles['previewContainer']}>
            <div className={styles['textPreview']}>
              "{clipboardContent.preview?.substring(0, 50)}{clipboardContent.preview && clipboardContent.preview.length > 50 ? '...' : ''}"
            </div>
            <span className={styles['label']}>Text ready to paste</span>
          </div>
        );
      
      case 'empty':
      default:
        return (
          <div className={styles['previewContainer']}>
            <div className={styles['emptyPreview']}>
              <span className={styles['emptyIcon']}>📋</span>
            </div>
            <span className={styles['label']}>No pasteable content</span>
          </div>
        );
    }
  };

  const groupedShortcuts = shortcuts.reduce((acc, shortcut) => {
    if (!acc[shortcut.category]) {
      acc[shortcut.category] = [];
    }
    acc[shortcut.category]!.push(shortcut);
    return acc;
  }, {} as Record<string, KeyboardShortcut[]>);

  return (
    <div className={styles['clipboardPreview']}>
      <div className={styles['header']}>
        <span className={styles['title']}>Clipboard</span>
        <button 
          className={styles['closeButton']}
          onClick={() => uiStore.setActiveTool('select')}
          title="Close (Switch to Select)"
        >
          <X size={14} />
        </button>
      </div>
      
      {renderPreview()}
      
      <div className={styles['instructions']}>
        Press Ctrl+V to paste
      </div>

      <div className={styles['shortcutsSection']}>
        <button 
          className={styles['shortcutsToggle']}
          onClick={() => setShowShortcuts(!showShortcuts)}
        >
          <span>Keyboard Shortcuts</span>
          <span className={`${styles['toggleIcon']} ${showShortcuts ? styles['toggleIconOpen'] : ''}`}>▼</span>
        </button>
        
        {showShortcuts && (
          <div className={styles['shortcutsList']}>
            {Object.entries(groupedShortcuts).map(([category, categoryShortcuts]) => (
              <div key={category} className={styles['shortcutCategory']} data-category={category}>
                <h4 className={styles['categoryTitle']}>
                  {category === 'tools' ? 'Tools' : 
                   category === 'actions' ? 'Actions' : 
                   category === 'stroke' ? 'Stroke Colors' : 
                   category === 'fill' ? 'Fill Colors' : 
                   'Navigation'}
                </h4>
                <div className={styles['categoryContent']}>
                  {categoryShortcuts.map((shortcut, index) => (
                    <ShortcutItem key={index} shortcut={shortcut} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}); 